import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { hasPermission, PERMISSIONS } from '../utils/permissions';
import ScreenshotCapture from '../components/Products/ScreenshotCapture';
// 需求 R129（PM 2026-08-21 拍板 Q1=C／Q2=A／Q3=A／Q4=B／Q5=A／Q7=A／Q9=B）：
// 必填欄位的定義只有這一份（畫面的 * 記號與送出驗證同源），不得在本頁另寫一組清單。
import { PRODUCT_REQUIRED_FIELDS, findMissingRequiredFields, isRequiredFieldFilled, requiredFieldMessage } from '../utils/productRequiredFields';
// 需求 R142（PM 2026-08-29 拍板 Q4=A／Q5=A）：下載圖片的檔名規則
// （產品名稱洗非法字元＋副檔名判定）抽在 utils，供本頁與 fixture 共用同一份定義。
// 需求 R152：下載邏輯已搬到 utils/productImageDownloader.js（見 handleDownloadImage 上方註解），
// 本頁不再直接用 buildImageFileName —— 檔名規則由那支統一套用，四個呼叫點共用同一份。
import { downloadProductImage } from '../utils/productImageDownloader';
// 需求 R147（PM 2026-08-29 Q8：「完全依照報價單顯示，也顯示幣別」）：
// 「歷史報價」的單價位數規則與報價單詳情頁（QuoteDetailPage 的 fmtDetailMoney）同源，
// 不在本頁另寫一套 toFixed —— 那正是 需求 R132 R5 在詳情頁抓到的缺陷（寫死 2 位不看幣別）。
import { ceilMoney, moneyDecimals } from '../utils/quoteMoney';
// 需求 R151（PM 2026-08-31 拍板 Q4=A「五個理由全部照算」）：
// 歷史報價每一列的「!」與報價單頁走同一套規則，判定所需的三支函式全部 import 既有 util，
// 本頁一行算式都不自己重寫（AC6「不新增第三套判定規則」）：
// ‧ getBaseCostFromSource：該列成本來源對應的**產品實時成本**（自訂成本要跟它比才算改過）
// ‧ packCostFromCustomBoxCost：自訂箱成本 →（÷箱入數）→ 包成本，換算式只存在於 quoteUnitPrice.js
import { getBaseCostFromSource, packCostFromCustomBoxCost } from '../utils/quoteUnitPrice';
// ‧ getDefaultPriceTier：本票把它從「建立頁／編輯頁各一份」抽成共用 util，本頁是第三個使用者
import { getDefaultPriceTier } from '../utils/quotePriceTier';
// 需求 R151 需求 2 的「$」：Q8=A（PM 明示接受「採購在產品詳細頁看得到 $」，
// ＝需求 R148 Q10=A 的實質結果「採購看不到 $」被本票推翻，兩票台帳同步更新）。
// AC7：一律沿用 utils/sensitivePriceAlert.js，不在本頁新增第二份敏感欄位清單。
// AC11：本頁 renderAudit 既有的 allLog.filter 判別式**逐字保留不動**（需求 R146 G1/G2
// 與 需求 R148 [9e] 雙重逐字鎖）——本票只新增 import，不把那段改寫成共用 util。
import { hasSensitivePriceChange, SENSITIVE_PRICE_ALERT_TITLE } from '../utils/sensitivePriceAlert';
import './ProductsPage.css';
// 需求 R151 AC16：「$」沿用 需求 R148 既有的 .price-alert-dot 樣式（不另開一份）。
import '../styles/sensitivePriceAlert.css';
// 需求 R147「歷史報價」區塊獨立 CSS（見檔頭註解）：ProductsPage.css 被
// tests/pageJump.fixture.js 快照鎖住 byte-equal，任何新增都會誤觸紅燈，
// 因此把本區塊獨有的 .pd-quote-link／.pd-qh-status*／等樣式抽到獨立檔。
import './ProductQuoteHistory.css';

const API = process.env.REACT_APP_API_URL || '/api';

// 需求 R147「歷史報價」常數（PM 2026-08-29 拍板 Q2／Q6）
/** Q6：初次載入 10 筆。 */
const QUOTE_HISTORY_FIRST_SIZE = 10;
/** Q6：之後每按一次「載入更多」追加 20 筆（10→30→50…），已載入的列不清空。 */
const QUOTE_HISTORY_MORE_SIZE = 20;
/**
 * Q2=A：草稿／待審核／已核准／審核退回**全部都列**，並在列上顯示狀態標籤。
 * 文案採 PM 留言的逐字用語（注意 rejected 在此為「審核退回」，與 QuotesPage 的
 * 「退回修改」不同 —— 那是列表頁既有文案，本票不動它）。
 */
const QUOTE_HISTORY_STATUS = {
  draft: { label: '草稿', className: 'pd-qh-status-draft' },
  pending: { label: '待審核', className: 'pd-qh-status-pending' },
  approved: { label: '已核准', className: 'pd-qh-status-approved' },
  rejected: { label: '審核退回', className: 'pd-qh-status-rejected' },
};

/**
 * 歷史報價的單價字串：先依幣別無條件進位再固定補足位數，與 QuoteDetailPage
 * fmtDetailMoney／匯出 fmtMoney 完全同一套規則（USD／EUR 2 位、TWD／HKD 1 位）。
 * AC12：無值／非數字回 '—'，不得印出 NaN／null／0。
 */
const fmtQuoteHistoryPrice = (value, currency) => {
  const ceiled = ceilMoney(value, currency);
  if (ceiled === null) return '—';
  return ceiled.toFixed(moneyDecimals(currency));
};

// 需求 R151 需求 2（PM 2026-08-31）：
// 「歷史報價紀錄中，若該商品在該報價單中有觸發『!』、『$』符號，在此紀錄裡也出現，
// 位置落在單價右側。」
// Q4=A 逐字：「五個理由全部照算，只要有亮就顯示。」
//
// 「!」＝ QuoteEditPage.js 品項單價欄那顆 .price-modified-dot 的 5 個 OR 條件，逐條同源：
// ① item.isManualPrice        單價已手動調整          （DB 存值，直接讀）
// ② item.isCostSourceChanged  成本來源已變更          （DB 存值，直接讀）
// ③ 自訂包成本已生效：customCostEnabled ＋ 值為有限正數 ＋ **與該來源的產品實時成本不同**
// ④ 自訂箱成本已生效：customBoxCostEnabled ＋ 換算後的包成本 ≠ 該來源的產品實時成本
// （比的是**換算後的包成本**而不是使用者填的箱成本——量級不同，直接比會整排誤報）
// ⑤ 價格階非預設：Number(priceTier) !== getDefaultPriceTier(客戶分級, 該列成本來源)
// defaultPriceTier 不在 Quote schema，報價單頁也是載入時現算，這裡用同一支 util 現算。
//
// 為什麼放在元件外層而不是塞進 JSX：這是一段**純函式**（同樣的 row + product 永遠同樣的結果），
// 拉出來才能被 tests/quoteHistoryAlerts.fixture.js 直接載進沙箱跑真值表，
// 而不是只能用 regex 看「JSX 裡有沒有寫這幾個字」。
//
// AC15 舊單容錯：需求 R133 之前的單沒有 customCostEnabled／需求 R149 之前沒有 customBoxCost*，
// 欄位缺漏一律走 falsy → 該理由不成立、不拋錯、不影響同區塊其他列（PM Q10=A：不做回填）。
// product 為 null（產品尚未載入完成）時 getBaseCostFromSource 回 null，③④ 自動不成立。
/**
 * 這一列在它所屬的報價單裡有沒有亮「!」。
 * @param {object} row  GET /api/quotes/by-product/:productId 回傳的單列
 * @param {object|null} product 產品實時資料（本頁的 fullProduct；歷史報價全是同一個產品）
 */
const hasQuoteHistoryPriceModified = (row, product) => {
  if (!row) return false;
  // ③④ 的比較基準：該列成本來源在**產品當下**的成本（與報價單頁 sourceCostForRow 同源）
  const sourceCost = getBaseCostFromSource(product, row.costSource);
  const customCostNum = Number(row.customCostValue);
  const isCustomCostActive =
    !!row.customCostEnabled
    && Number.isFinite(customCostNum)
    && customCostNum > 0
    && customCostNum !== Number(sourceCost);
  const boxPackCost = packCostFromCustomBoxCost(row.customBoxCostValue, row.pcsPerBox);
  const isCustomBoxCostActive =
    !!row.customBoxCostEnabled
    && boxPackCost != null
    && boxPackCost !== Number(sourceCost);
  const defaultTier = getDefaultPriceTier(row.customerLevel, row.costSource);
  const isPriceTierChanged = Number(row.priceTier) !== Number(defaultTier);
  return !!row.isManualPrice || !!row.isCostSourceChanged
    || isCustomCostActive || isCustomBoxCostActive || isPriceTierChanged;
};

// Helpers (same as ProductModal)
const emptyForm = () => ({
  systemCode: '',
  productCode: '',
  productName: '',
  englishName: '',
  series: '',
  category: '',
  tags: [],
  spec: '',
  pcsPerBox: '',
  barcode: '',
  productImage: '',
  vegetarianType: '',
  shelfLife: '',
  retailPrice: '',
  sealingColor: '',
  salesNotes: '',
  hkExcluded: false,
  netWeight: '',
  grossWeight: '',
  boxLength: '',
  boxWidth: '',
  boxHeight: '',
  volumeMethod: 'auto',
  volumeCuft: '',
  volumeCbm: '',
  costSource: 'factory',
  factoryCost: { supplierName: '', boxPrice: '', unitPrice: '', moq: '', notes: '', shippableCountries: [], nonShippableCountries: [] },
  distributorCosts: [],
  hkMarkup: false,
  hkReferencePrice: '',
  hkShippingCostPerPack: '',
  hkShippingCostPerBox: '',
  hkTaiwanPrice: '',
  hkLogisticsCost: '',
  hkShippingFee: '',
  hkInboundCost: '',
  purchaseCostUnit: '',
  labelable: '',
  labelFee: '',
  purchaseNotes: '',
  productStatus: '',
  catalogStatus: '',
  websiteStatus: '',
});

const emptyDistributor = () => ({ boxPrice: '', unitPrice: '', moq: '', notes: '', shippableCountries: [], nonShippableCountries: [] });

const calcUnitPrice = (boxPrice, pcsPerBox) => {
  const bp = parseFloat(boxPrice);
  const pcs = parseFloat(pcsPerBox);
  if (!bp || !pcs || pcs === 0) return '';
  return (Math.ceil(bp / pcs * 10) / 10).toFixed(1);
};

const calcCuft = (l, w, h) => {
  const L = parseFloat(l), W = parseFloat(w), H = parseFloat(h);
  if (!L || !W || !H) return '';
  return (Math.round((L * W * H / 28317) * 100) / 100).toFixed(2);
};

const calcCbm = (cuft) => {
  const c = parseFloat(cuft);
  if (!c) return '';
  return (Math.round((c / 35.315) * 1000) / 1000).toFixed(3);
};

// 出貨國家切成標籤顯示。PM 2026-05-29 確認：分隔符只認「/」，「、」不得視為切分字元
// （避免把含「、」的單一國名誤切；匯入規範亦以「/」回存，顯示↔儲存往返統一為「/」）。
const parseCountries = (val) => {
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val === 'string' && val.trim()) return val.split('/').map((s) => s.trim()).filter(Boolean);
  return [];
};

// 無條件進位至小數第一位
const ceil1 = (n) => Math.ceil(n * 10) / 10;

const calcHK = (form, hkdRate) => {
  if (!hkdRate) return {};
  const rate = parseFloat(hkdRate);
  if (!rate) return {};
  const pcs = parseFloat(form.pcsPerBox) || 0;
  const cuft = parseFloat(form.volumeCuft) || 0;
  let boxPrice = 0;
  if (form.costSource === 'factory') {
    boxPrice = parseFloat(form.factoryCost?.boxPrice) || 0;
  } else if (form.costSource?.startsWith('distributor_')) {
    const idx = parseInt(form.costSource.split('_')[1], 10);
    boxPrice = parseFloat(form.distributorCosts?.[idx]?.boxPrice) || 0;
  } else if (form.costSource === 'distributor' && form.distributorCosts?.length > 0) {
    boxPrice = parseFloat(form.distributorCosts[0]?.boxPrice) || 0;
  }
  if (!boxPrice || !rate) return {};
  const hkTaiwanPrice = form.hkMarkup ? ceil1(boxPrice * 1.05 / rate) : ceil1(boxPrice / rate);
  const hkShippingFee = cuft ? ceil1(10 * cuft) : 0;
  // 進倉成本: 台灣進價 + 船務成本（需求 R50，PM 2026-06-17 Q3=A 維持物流/出貨公式不改）
  const hkInboundCost = ceil1(hkTaiwanPrice + hkShippingFee);
  const hkLogisticsCost = Math.min(ceil1((hkShippingFee + hkTaiwanPrice) * 0.035), 15);
  const hkShippingCostPerBox = ceil1(hkLogisticsCost + hkShippingFee + hkTaiwanPrice);
  const hkShippingCostPerPack = pcs ? ceil1(hkShippingCostPerBox / pcs) : 0;
  const hkReferencePrice = ceil1(hkShippingCostPerBox / 0.75);
  return { hkTaiwanPrice, hkShippingFee, hkInboundCost, hkLogisticsCost, hkShippingCostPerBox, hkShippingCostPerPack, hkReferencePrice };
};

// 需求 R70（PM 2026-07-19 拍板）：金額欄位一律「最多到小數點第一位、
// 無條件進位」，顯示固定一位小數（整數補 .0，例：350 → 350.0；813.7499999999999 → 813.8；
// 32.60 → 32.6）。先以 toFixed(6) 清浮點雜訊再 ×10 無條件進位、÷10 還原，最後 toFixed(1)。
const fmtMoney = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const n = parseFloat(v);
  if (isNaN(n)) return '';
  const scaled = Number((n * 10).toFixed(6));
  return (Math.ceil(scaled) / 10).toFixed(1);
};

// Option Manager sub-component
const OptionManager = ({ type, label, options, onOptionsChange, canEdit, onClose }) => {
  const [newValue, setNewValue] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const token = () => localStorage.getItem('token');
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const addOption = async () => {
    if (!newValue.trim()) return;
    try {
      const res = await fetch(`${API}/products/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ type, value: newValue.trim() }),
      });
      if (res.ok) {
        const created = await res.json();
        onOptionsChange([...options, created]);
        setNewValue('');
      }
    } catch (err) { console.error(err); }
  };

  const saveEdit = async (id) => {
    if (!editingValue.trim()) return;
    try {
      const res = await fetch(`${API}/products/options/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ value: editingValue.trim() }),
      });
      if (res.ok) {
        const updated = await res.json();
        onOptionsChange(options.map((o) => (o._id === id ? updated : o)));
        setEditingId(null);
      }
    } catch (err) { console.error(err); }
  };

  const deleteOption = async (id) => {
    try {
      const res = await fetch(`${API}/products/options/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) {
        onOptionsChange(options.filter((o) => o._id !== id));
      } else {
        const data = await res.json();
        alert(data.message || '刪除失敗');
      }
    } catch (err) { console.error(err); }
  };

  return (
    <div className="option-manager-panel" ref={panelRef}>
      <div className="option-manager-header">
        <span>管理「{label}」選項</span>
        <span style={{ fontSize: '11px' }}>{options.length} 個選項</span>
      </div>
      <div style={{ maxHeight: 140, overflowY: 'auto' }}>
        {options.map((o) => (
          <div key={o._id} className="option-item">
            {editingId === o._id ? (
              <>
                <input className="option-item-input" value={editingValue} onChange={(e) => setEditingValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') setEditingId(null); }} autoFocus />
                <button className="option-item-btn save-btn" onClick={() => saveEdit(o._id)}>&#10003;</button>
                <button className="option-item-btn cancel-btn" onClick={() => setEditingId(null)}>&#10005;</button>
              </>
            ) : (
              <>
                <span className="option-item-value">{o.value}</span>
                {canEdit && (
                  <>
                    <button className="option-item-btn edit-btn" onClick={() => { setEditingId(o._id); setEditingValue(o.value); }}>編輯</button>
                    <button className="option-item-btn delete-btn" onClick={() => deleteOption(o._id)}>刪除</button>
                  </>
                )}
              </>
            )}
          </div>
        ))}
        {options.length === 0 && <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', padding: '4px 0' }}>尚無選項</div>}
      </div>
      {canEdit && (
        <div className="option-add-row">
          <input className="option-add-input" placeholder="輸入新選項..." value={newValue} onChange={(e) => setNewValue(e.target.value)} />
          <button className="option-add-btn" onClick={addOption}>+ 新增</button>
        </div>
      )}
    </div>
  );
};

// Main Page Component
const ProductDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const isCreate = !id;
  const initialMode = isCreate ? 'add' : (searchParams.get('mode') === 'edit' ? 'edit' : 'view');

  const [form, setForm] = useState(emptyForm());
  const [currentMode, setCurrentMode] = useState(initialMode);
  const [activeNav, setActiveNav] = useState('基本資訊');
  const [fullProduct, setFullProduct] = useState(null);

  // Options state
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  // 需求 R59（PM 2026-07-04 拍板 D3=A / D6=B）：
  // 葷素備註改純文字自由輸入 — 移除 vegetarianOptions 下拉來源與 OptionManager 管理入口。
  // R3 UI 防禦（disabled option inject）隨 select→text 自然失效。
  // 需求 R147「歷史報價」（唯讀，AC16：**刻意不進 form state**，故獨立於 form 之外，
  // 也不出現在 handleSave 的 payload 裡）。
  const [quoteHistory, setQuoteHistory] = useState([]);
  const [quoteHistoryTotal, setQuoteHistoryTotal] = useState(0);
  const [quoteHistoryLoading, setQuoteHistoryLoading] = useState(false);
  // AC7：連點兩次「載入更多」不得產生重複列／重複請求。button 的 disabled 只擋得住
  // 「重新 render 之後」的第二次點擊，同一個 tick 內的連點要靠 ref 同步擋下來
  // （setState 是非同步的，quoteHistoryLoading 在下一次 render 前還是 false）。
  const quoteHistoryBusyRef = useRef(false);
  const [openManagers, setOpenManagers] = useState({});
  const [tagsDropdownOpen, setTagsDropdownOpen] = useState(false);
  const tagsDropdownRef = useRef(null);
  const [hkdRate, setHkdRate] = useState(null);
  const pageRef = useRef(null);
  const headerRef = useRef(null);
  // 需求 R129：未通過驗證的必填欄位 key（依畫面順序），驅動紅框＋欄位下方紅字（Q4=B）。
  const [missingKeys, setMissingKeys] = useState([]);
  // 每個必填欄位登記兩個 DOM：容器（捲動定位用，含 label 一起露出）與輸入框（focus 用）。
  const requiredBoxRefs = useRef({});
  const requiredInputRefs = useRef({});

  // 動態量測 sticky 頁首實際高度，寫入 CSS 變數，讓 .pd-tab-nav 能精準凍結於頁首正下方。
  // Why +6px：.pd-page-header 的 box-shadow（--shadow-medium: 0 4px 10px）會向下延伸約 14px，
  // 但 getBoundingClientRect() 不包含 box-shadow，若直接以量測高度做 sticky top，
  // tab 列頂部會被頁首陰影覆蓋（即 PM 回報「凍結欄位上緣會被頁首遮住」的成因）。
  // 此處補上陰影視覺位移的上界，確保 tab 列能完整露出於頁首陰影下方。
  useLayoutEffect(() => {
    const pageEl = pageRef.current;
    const headerEl = headerRef.current;
    if (!pageEl || !headerEl) return undefined;
    const SHADOW_BUFFER = 6;
    const apply = () => {
      const h = Math.ceil(headerEl.getBoundingClientRect().height);
      if (h > 0) pageEl.style.setProperty('--pd-header-height', `${h + SHADOW_BUFFER}px`);
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(headerEl);
    return () => ro.disconnect();
  }, []);

  // Auth
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!token || !user._id) { navigate('/login'); return; }
    setCurrentUser(user);
  }, [navigate]);

  const fetchOptions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/products/options`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const all = await res.json();
        setSeriesOptions(all.filter((o) => o.type === 'series'));
        setCategoryOptions(all.filter((o) => o.type === 'category'));
        setTagOptions(all.filter((o) => o.type === 'tag'));
        // 需求 R59：vegetarianType 已改純文字輸入，不再取用選項清單。
      }
    } catch (err) { console.error(err); }
  }, []);

  const fetchExchangeRate = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/exchange-rates`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const rates = await res.json();
        const hk = rates.find((r) => r.key === 'hkd_internal');
        if (hk) setHkdRate(hk.rate);
      }
    } catch (err) { console.error(err); }
  }, []);

  // 需求 R151 R2（PM 2026-09-01 逐字：「本輪要針對Q8 做修正，採購看不到
  // 「歷史報價區塊」因此也不會看到警示符號」）：
  // **推翻** 需求 R147 Q3=B（「採購看得到區塊，只是單號不可點」）與本票 R1 的 Q8=A
  // （「採購看得到『$』」）。PM 這一句同時給了理由與結論：採購根本看不到整個歷史報價
  // 區塊，所以「採購會不會看到警示符號」這個問題自然消失。
  // 落地成三層（缺一層都會有殘留可見性）：
  // ① 本旗標為 false 時完全不發 GET /api/quotes/by-product（連 network tab 都看不到）；
  // ② TABS 的 visible 掛上它 → 頂部錨點導覽沒有「歷史報價」按鈕、區塊不渲染；
  // ③ renderQuoteHistory 開頭再擋一次（就算日後有人漏掉 ②）。
  // 後端 quoteController.getByProduct 另有第四層（採購一律 403），見該函式檔頭。
  // 這裡刻意用 currentUser?.role 而不是下面第 625 行的 role：那個 const 宣告在本
  // useCallback **之後**，寫進相依陣列會在 render 當下踩到 TDZ。
  const canViewQuoteHistory = currentUser?.role !== 'purchasing';

  // 需求 R147「歷史報價」：抓取（AC5-AC7、AC15、AC18）
  //
  // offset 語意見 quoteController.getByProduct 的檔頭：Q6 的 10→+20→+20 節奏用 page
  // 表達不出來（page=2&limit=20 會 skip 20 而漏掉第 11-20 列），故一律送「已載入列數」。
  // AC15：isCreate（/products/new）時本函式不會被呼叫，effect 端就先擋掉了。
  const fetchQuoteHistory = useCallback(async (offset) => {
    if (!id) return;
    // 需求 R151 R2 第①層：採購不發任何歷史報價請求。放在函式**內部**而非呼叫端，
    // 是為了同時蓋掉「載入更多」按鈕那個呼叫點（雖然該按鈕在採購眼中根本不存在）。
    if (!canViewQuoteHistory) return;
    if (quoteHistoryBusyRef.current) return; // AC7 同 tick 連點防線
    const token = localStorage.getItem('token');
    if (!token) return;
    quoteHistoryBusyRef.current = true;
    setQuoteHistoryLoading(true);
    const limit = offset === 0 ? QUOTE_HISTORY_FIRST_SIZE : QUOTE_HISTORY_MORE_SIZE;
    try {
      const res = await fetch(`${API}/quotes/by-product/${id}?offset=${offset}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const rows = Array.isArray(data.rows) ? data.rows : [];
        // AC6：既有列不清空、以 append 方式追加；offset === 0 才是整批重來（首次載入）。
        setQuoteHistory((prev) => (offset === 0 ? rows : [...prev, ...rows]));
        setQuoteHistoryTotal(Number(data.total) || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      quoteHistoryBusyRef.current = false;
      setQuoteHistoryLoading(false);
    }
    // canViewQuoteHistory 進相依陣列：登入者資料是非同步載入的，role 從 undefined
    // 變成 'purchasing' 之後這支必須跟著換身分（否則第一次 render 的舊閉包會漏發／誤發）。
  }, [id, canViewQuoteHistory]);

  const populateForm = (data) => {
    setForm({
      systemCode: data.systemCode || '',
      productCode: data.productCode || '',
      productName: data.productName || '',
      englishName: data.englishName || '',
      series: data.series || '',
      category: data.category || '',
      tags: data.tags || [],
      spec: data.spec || '',
      pcsPerBox: data.pcsPerBox ?? '',
      barcode: data.barcode || '',
      productImage: data.productImage || '',
      vegetarianType: data.vegetarianType || '',
      shelfLife: data.shelfLife || '',
      retailPrice: data.retailPrice ?? '',
      sealingColor: data.sealingColor || '',
      salesNotes: data.salesNotes || '',
      hkExcluded: data.hkExcluded || false,
      netWeight: data.netWeight ?? '',
      grossWeight: data.grossWeight ?? '',
      boxLength: data.boxLength ?? '',
      boxWidth: data.boxWidth ?? '',
      boxHeight: data.boxHeight ?? '',
      volumeMethod: data.volumeMethod || 'auto',
      volumeCuft: data.volumeCuft ?? '',
      volumeCbm: data.volumeCbm ?? '',
      costSource: data.costSource === 'distributor' ? 'distributor_0' : (data.costSource || 'factory'),
      factoryCost: {
        supplierName: data.factoryCost?.supplierName || '',
        // 需求 R70：既有資料箱價可能帶浮點雜訊（813.7499999999999）且從未 onBlur 過，
        // 載入即以 fmtMoney 正規化（無條件進位至一位）→ 顯示乾淨、存檔亦持久化正確值。
        boxPrice: fmtMoney(data.factoryCost?.boxPrice ?? ''),
        unitPrice: data.factoryCost?.unitPrice ?? '',
        moq: data.factoryCost?.moq || '',
        notes: data.factoryCost?.notes || '',
        shippableCountries: parseCountries(data.factoryCost?.shippableCountries),
        nonShippableCountries: parseCountries(data.factoryCost?.nonShippableCountries),
      },
      distributorCosts: (data.distributorCosts || []).map((d) => ({
        supplierName: d.supplierName || '',
        boxPrice: fmtMoney(d.boxPrice ?? ''), // 需求 R70：同工廠成本，載入即正規化
        unitPrice: d.unitPrice ?? '',
        moq: d.moq || '',
        notes: d.notes || '',
        shippableCountries: parseCountries(d.shippableCountries),
        nonShippableCountries: parseCountries(d.nonShippableCountries),
      })),
      purchaseCostUnit: data.purchaseCostUnit ?? '',
      labelable: data.labelable ?? '',
      labelFee: data.labelFee ?? '',
      purchaseNotes: data.purchaseNotes ?? '',
      hkMarkup: data.hkMarkup ?? true,
      hkReferencePrice: data.hkReferencePrice ?? '',
      hkShippingCostPerPack: data.hkShippingCostPerPack ?? '',
      hkShippingCostPerBox: data.hkShippingCostPerBox ?? '',
      hkTaiwanPrice: data.hkTaiwanPrice ?? '',
      hkLogisticsCost: data.hkLogisticsCost ?? '',
      hkShippingFee: data.hkShippingFee ?? '',
      hkInboundCost: data.hkInboundCost ?? '',
      productStatus: data.productStatus || '',
      catalogStatus: data.catalogStatus || '',
      websiteStatus: data.websiteStatus || '',
    });
  };

  // Fetch product data
  useEffect(() => {
    if (!currentUser) return;
    fetchOptions();
    fetchExchangeRate();

    if (isCreate) {
      setLoading(false);
      return;
    }

    // 需求 R147 AC5／AC15：只有既有產品（有 _id）才載入歷史報價，且首批固定 10 筆。
    // 放在 isCreate 早退之後 ＝ /products/new 不會發出任何歷史報價請求。
    fetchQuoteHistory(0);

    const fetchProduct = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${API}/products/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setFullProduct(data);
          populateForm(data);
        } else {
          navigate('/products');
        }
      } catch {
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [currentUser, id, isCreate, navigate, fetchOptions, fetchExchangeRate, fetchQuoteHistory]);

  // Auto-calculate CUFT/CBM
  useEffect(() => {
    if (form.volumeMethod === 'auto') {
      const cuft = calcCuft(form.boxLength, form.boxWidth, form.boxHeight);
      const cbm = calcCbm(cuft);
      setForm((prev) => ({ ...prev, volumeCuft: cuft, volumeCbm: cbm }));
    }
  }, [form.volumeMethod, form.boxLength, form.boxWidth, form.boxHeight]);

  // 需求 R129 R4（2026-08-21）：原本這裡多一道 `&& form.volumeCuft !== ''` 的守門，
  // 導致「廠商提供／預估值」模式下把 CUFT 清空時不會重算，volumeCbm 停在上一次的舊值。
  // 在 Q2=A（材積以 volumeCbm > 0 判定有填）之下，這個殘值會直接變成必填驗證的繞道：
  // 來源欄位已經空了、畫面 CUFT 顯示「-」，儲存卻照樣過關，紅框也不會出現（違反 AC10／AC12）。
  // 改為 method !== 'auto' 就無條件重算——calcCbm('') 回 ''，清空 CUFT 即同步清空 CBM。
  useEffect(() => {
    if (form.volumeMethod !== 'auto') {
      const cbm = calcCbm(form.volumeCuft);
      setForm((prev) => (prev.volumeCbm === cbm ? prev : { ...prev, volumeCbm: cbm }));
    }
  }, [form.volumeMethod, form.volumeCuft]);

  // Auto-calculate factory unit price
  useEffect(() => {
    const up = calcUnitPrice(form.factoryCost?.boxPrice, form.pcsPerBox);
    setForm((prev) => ({
      ...prev,
      factoryCost: { ...prev.factoryCost, unitPrice: up },
    }));
  }, [form.factoryCost?.boxPrice, form.pcsPerBox]);

  // Auto-calculate distributor unit prices
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      distributorCosts: prev.distributorCosts.map((d) => ({
        ...d,
        unitPrice: calcUnitPrice(d.boxPrice, prev.pcsPerBox),
      })),
    }));
  }, [form.pcsPerBox]); // eslint-disable-line

  const isView = currentMode === 'view';
  const role = currentUser?.role;
  const canBasicEdit = hasPermission(role, PERMISSIONS.PRODUCT_BASIC_EDIT);
  const canLogisticsEdit = hasPermission(role, PERMISSIONS.PRODUCT_LOGISTICS_EDIT);
  const canCostView = hasPermission(role, PERMISSIONS.PRODUCT_COST_VIEW);
  const canHKView = hasPermission(role, PERMISSIONS.PRODUCT_HK_VIEW);
  const canMarketingEdit = hasPermission(role, PERMISSIONS.PRODUCT_MARKETING_EDIT);
  // 需求 R4（PM 2026-05-08 補充指示）：
  // 採購可編輯「產品狀態(上架/下架)」與「香港不出」，但目錄/官網狀態仍只有主管可改，
  // 故拆出獨立權限分別控制。
  const canProductStatusEdit = hasPermission(role, PERMISSIONS.PRODUCT_STATUS_EDIT);
  const canHkExcludedEdit = hasPermission(role, PERMISSIONS.PRODUCT_HK_EXCLUDED_EDIT);
  // 需求 R157（PM 2026-09-01 拍板 Q1~Q6 全 A）：「稽核紀錄」開放給所有角色
  // （manager／purchasing／sales）。**推翻** 需求 R146 時代「只有主管看得到」的現況。
  // Q2=A：權限鍵 PRODUCT_AUDIT_VIEW 保留，本行一個字都不改——放開的是 permissions.js
  // 的角色陣列。禁止改成寫死 true 或 role 字串比對（fixture 會紅）。
  // Q3=A：採購／業務看到的內容與主管完全一致（日期／修改人／欄位／修改前／修改後五欄），
  // renderAudit 不做任何角色分流。
  // Q4=A：不與 canCostView 相依。
  const canAuditView = hasPermission(role, PERMISSIONS.PRODUCT_AUDIT_VIEW);

  const hkValues = canHKView ? calcHK(form, hkdRate) : {};

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateFactoryCost = (field, value) => setForm((prev) => ({ ...prev, factoryCost: { ...prev.factoryCost, [field]: value } }));
  const updateDistributorCost = (idx, field, value) => {
    setForm((prev) => {
      const costs = [...prev.distributorCosts];
      costs[idx] = { ...costs[idx], [field]: value };
      if (field === 'boxPrice') costs[idx].unitPrice = calcUnitPrice(value, prev.pcsPerBox);
      return { ...prev, distributorCosts: costs };
    });
  };

  const addDistributor = () => {
    if (form.distributorCosts.length >= 3) return;
    setForm((prev) => ({ ...prev, distributorCosts: [...prev.distributorCosts, emptyDistributor()] }));
  };

  const removeDistributor = (idx) => {
    setForm((prev) => {
      const newCosts = prev.distributorCosts.filter((_, i) => i !== idx);
      let newCostSource = prev.costSource;
      if (prev.costSource === `distributor_${idx}`) {
        newCostSource = 'factory';
      } else if (prev.costSource?.startsWith('distributor_')) {
        const selectedIdx = parseInt(prev.costSource.split('_')[1], 10);
        if (selectedIdx > idx) newCostSource = `distributor_${selectedIdx - 1}`;
      }
      return { ...prev, distributorCosts: newCosts, costSource: newCostSource };
    });
  };

  const toggleTag = (tag) => {
    setForm((prev) => {
      const tags = prev.tags.includes(tag) ? prev.tags.filter((t) => t !== tag) : [...prev.tags, tag];
      return { ...prev, tags };
    });
  };

  const toggleManager = (key) => setOpenManagers((prev) => ({ ...prev, [key]: !prev[key] }));
  const closeManager = (key) => setOpenManagers((prev) => ({ ...prev, [key]: false }));

  useEffect(() => {
    const handler = (e) => {
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(e.target)) {
        setTagsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── 需求 R129：必填欄位驗證（PM 2026-08-21 留言拍板）
  // Q4=B：不用 alert，改「欄位級紅框＋欄位下方紅字訊息＋自動跳到第一個未填欄位」。
  // Q5=A：一次只跳第一個，使用者補完再按儲存才跳下一個。
  const registerRequiredBox = (key) => (el) => { requiredBoxRefs.current[key] = el; };
  const registerRequiredInput = (key) => (el) => { requiredInputRefs.current[key] = el; };
  const isMissing = (key) => missingKeys.includes(key);
  const requiredClass = (key) => `form-control${isMissing(key) ? ' pd-field-invalid' : ''}`;
  const renderRequiredError = (key) => {
    if (!isMissing(key)) return null;
    const field = PRODUCT_REQUIRED_FIELDS.find((f) => f.key === key);
    return <div className="pd-field-error-msg">{requiredFieldMessage(field ? field.label : '此欄位')}</div>;
  };

  // AC10：使用者一補上內容，紅框／紅字立刻解除，不必重按儲存。
  useEffect(() => {
    setMissingKeys((prev) => {
      if (!prev.length) return prev;
      const next = prev.filter((k) => !isRequiredFieldFilled(k, form[k]));
      return next.length === prev.length ? prev : next;
    });
  }, [form]);

  // AC9：.pd-page-header 與 .pd-tab-nav 都是 sticky，捲到欄位時要把兩者的「實際高度」扣掉，
  // 否則目標欄位會剛好被壓在導覽列底下。高度一律即時量測，不寫死 px。
  // focus 用 preventScroll，避免瀏覽器又把元素拉到自己想要的位置、蓋掉上面算好的位移。
  const scrollToRequiredField = (key) => {
    const box = requiredBoxRefs.current[key] || requiredInputRefs.current[key];
    const input = requiredInputRefs.current[key];
    if (box) {
      const headerH = headerRef.current ? headerRef.current.getBoundingClientRect().height : 0;
      const navEl = pageRef.current ? pageRef.current.querySelector('.pd-tab-nav') : null;
      const navH = navEl ? navEl.getBoundingClientRect().height : 0;
      const GAP = 12;
      const top = window.pageYOffset + box.getBoundingClientRect().top - headerH - navH - GAP;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }
    if (input && typeof input.focus === 'function') {
      try { input.focus({ preventScroll: true }); } catch (err) { input.focus(); }
    }
  };

  const handleSave = async ({ returnToList = false } = {}) => {
    // AC13：檢視模式不驗證也不標示（三顆儲存鈕在檢視模式本來就不渲染，這裡是第二層保險）。
    // 註：權限矩陣中沒有「有 PRODUCT_BASIC_EDIT 但沒有 PRODUCT_LOGISTICS_EDIT」的角色
    // （manager／purchasing 兩者皆有、sales 兩者皆無），因此七個必填欄位一律驗證，
    // 不會出現「被自己改不到的欄位擋住」的死結。
    if (isView) return;
    // Q7=A：三顆按鈕（儲存變更／儲存草稿／儲存並返回）一致擋下，草稿也不例外。
    // AC5：驗證放在 window.confirm 之前 → 驗證失敗連「確認儲存並返回」都不會跳。
    const missing = findMissingRequiredFields(form);
    if (missing.length > 0) {
      setMissingKeys(missing.map((f) => f.key));
      scrollToRequiredField(missing[0].key);
      return;
    }
    setMissingKeys([]);
    if (returnToList && !window.confirm('確認儲存並返回產品列表頁面？')) return;

    // 回存以「/」分隔，與 需求 R39 匯入規範一致（PM 2026-05-29 確認可/不可出貨國家分隔符為「/」）。
    const serializeCountries = (arr) => (Array.isArray(arr) ? arr.join('/') : (arr || ''));
    const saveData = {
      ...form,
      factoryCost: {
        ...form.factoryCost,
        shippableCountries: serializeCountries(form.factoryCost.shippableCountries),
        nonShippableCountries: serializeCountries(form.factoryCost.nonShippableCountries),
      },
      distributorCosts: form.distributorCosts.map((d) => ({
        ...d,
        shippableCountries: serializeCountries(d.shippableCountries),
        nonShippableCountries: serializeCountries(d.nonShippableCountries),
      })),
      hkReferencePrice: hkValues.hkReferencePrice ?? form.hkReferencePrice,
      hkShippingCostPerPack: hkValues.hkShippingCostPerPack ?? form.hkShippingCostPerPack,
      hkShippingCostPerBox: hkValues.hkShippingCostPerBox ?? form.hkShippingCostPerBox,
      hkTaiwanPrice: hkValues.hkTaiwanPrice ?? form.hkTaiwanPrice,
      hkLogisticsCost: hkValues.hkLogisticsCost ?? form.hkLogisticsCost,
      hkShippingFee: hkValues.hkShippingFee ?? form.hkShippingFee,
      hkInboundCost: hkValues.hkInboundCost ?? form.hkInboundCost,
      hkExcluded: form.hkExcluded,
    };

    const token = localStorage.getItem('token');
    const url = isCreate ? `${API}/products` : `${API}/products/${id}`;
    const method = isCreate ? 'POST' : 'PUT';
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(saveData),
      });
      if (res.ok) {
        const saved = await res.json();
        setFullProduct(saved);
        populateForm(saved);
        setCurrentMode('view');
        if (isCreate) {
          alert('產品新增成功！');
          if (returnToList) {
            navigate('/products');
          } else {
            navigate(`/products/${saved._id}`, { replace: true });
          }
        } else {
          alert('產品儲存成功！');
          if (returnToList) navigate('/products');
        }
      } else {
        const err = await res.json();
        alert(err.message || '儲存失敗');
      }
    } catch (err) {
      console.error('儲存失敗', err);
    }
  };

  const enableEdit = () => setCurrentMode('edit');
  const cancelEdit = () => {
    if (isCreate) {
      navigate('/products');
    } else {
      setCurrentMode('view');
      if (fullProduct) populateForm(fullProduct);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const fmtAuditVal = (v) => {
    if (v === null || v === undefined || v === '' || v === 'null' || v === 'undefined') return '—';
    return String(v);
  };

  if (!currentUser || loading) return null;
  if (!isCreate && !fullProduct && !loading) return null;

  const TABS = [
    { label: '基本資訊', icon: '\u{1F4E6}', visible: true },
    { label: '物流屬性', icon: '\u{1F69A}', visible: true },
    { label: '成本價格', icon: '\u{1F4B0}', visible: canCostView },
    { label: '香港專用', icon: '\u{1F1ED}\u{1F1F0}', visible: canHKView },
    { label: '行銷狀態', icon: '\u{1F4CA}', visible: true },
    // 需求 R147（PM 2026-08-29 拍板 Q1=A：「緊接在行銷狀態區塊之後、
    // 稽核紀錄之前」）：本陣列的順序同時決定頂部錨點導覽的按鈕順序與區塊渲染順序
    // （visibleTabs.map 兩處都吃它），所以位置只要在這裡排對即可。
    // visible 用 !isCreate 而非 true：AC15 規定 /products/new 不渲染本區塊。
    // 需求 R151 R2（PM 2026-09-01 逐字：「本輪要針對Q8 做修正，採購
    // 看不到「歷史報價區塊」因此也不會看到警示符號」）**推翻** 需求 R147 Q3=B 的
    // 「採購也看得到區塊，故不掛任何權限旗標」——現在必須掛 canViewQuoteHistory
    // （＝ role !== 'purchasing'）。TABS 的 visible 同時決定頂部錨點導覽按鈕與區塊
    // 是否渲染（visibleTabs.map 兩處都吃它），所以擋在這一行就等於整塊消失。
    // 單號可不可點的 canOpenQuote 仍留在 renderQuoteHistory 裡當第三層防線（見該處）。
    { label: '歷史報價', icon: '\u{1F4DC}', visible: !isCreate && canViewQuoteHistory },
    // 需求 R157（PM 2026-09-01 Q1=A）：canAuditView 現在三個角色皆為 true，故採購／業務
    // 也會看到「異動紀錄」錨點按鈕與區塊。visible 同時決定錨點按鈕與區塊渲染，故一行即控整塊。
    // Q5=A 確認：上一行「歷史報價」對採購仍是隱藏（需求 R151），與本行方向相反是刻意的，
    // 兩者標的不同（報價單金額 vs 產品成本異動），不要「順手對齊」把其中一行改掉。
    { label: '異動紀錄', icon: '\u{1F4CB}', visible: canAuditView },
  ];

  const visibleTabs = TABS.filter((t) => t.visible);

  const headerSubtitle = [form.productName, form.spec, form.pcsPerBox ? `箱入數: ${form.pcsPerBox}` : ''].filter(Boolean).join(' / ');

  // 需求 R142：下載產品圖片（PM 2026-08-29 拍板 Q1=A 只做本頁）
  //
  // 需求 R152（PM 2026-09-01 留言）：需求擴散到「報價單-詳情頁（已審核
  // 通過鎖住的頁面）、產品管理、型錄管理」的產品縮圖，且明確要求「下載結果與在產品詳細頁
  // 執行結果相同」。因此本頁原本近 45 行的 fetch → blob → a.download＋三條退場路徑
  // **整段搬到 utils/productImageDownloader.js**（alert 文案、判斷順序、a.download 流程
  // 逐字不變，只把 form.productImage／form.productName 換成參數），本頁改為呼叫它。
  // 各縮圖用的 components/Products/ProductImageDownloadButton.js 呼叫的是同一支，
  // 「相同」因此是結構上保證的，而不是靠兩份程式碼各自維持一致。
  // 搬移的原因與三條退場路徑為何不能合併，見該檔檔頭。
  const handleDownloadImage = () => downloadProductImage(form.productImage, form.productName);

  // Tab renderers

  const renderBasicInfo = () => {
    const disabled = isView || !canBasicEdit;
    return (
      <section id="section-基本資訊" className="pd-section-card">
        <div className="pd-section-header">
          <h2 className="pd-section-title">
            <span className="pd-section-icon">{'\u{1F4E6}'}</span>
            產品基本資訊
          </h2>
          <span className="pd-section-badge pd-badge-info">一般權限</span>
        </div>
        <div className="pd-section-body">
          {/* Row 1: 系統辨識碼, 產品貨編, 產品系列, 產品類別 */}
          <div className="pd-form-grid pd-form-grid-4" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <div className="form-group">
              <label>系統辨識碼</label>
              <input type="text" className="form-control" value={form.systemCode} disabled readOnly placeholder="建立時自動產生" />
            </div>
            <div className="form-group">
              <label>產品貨編</label>
              <input type="text" className="form-control" value={form.productCode} onChange={(e) => updateField('productCode', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="非必填，例如：FQ-001" />
            </div>

            {[
              { key: 'series', label: '產品系列', opts: seriesOptions, setOpts: setSeriesOptions, type: 'series' },
              { key: 'category', label: '產品類別', opts: categoryOptions, setOpts: setCategoryOptions, type: 'category' },
            ].map(({ key, label, opts, setOpts, type }) => (
              <div key={key} className="form-group">
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {label}
                  {!disabled && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-info)', cursor: 'pointer' }} onClick={() => toggleManager(key)}>+ 管理選項</span>}
                </label>
                <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                  <select className="form-control" value={form[key]} onChange={(e) => updateField(key, e.target.value)} disabled={disabled} style={{ flex: 1 }}>
                    <option value="">請選擇{label.replace('產品', '')}</option>
                    {opts.map((o) => <option key={o._id} value={o.value}>{o.value}</option>)}
                  </select>
                </div>
                {!disabled && openManagers[key] && (
                  <OptionManager type={type} label={label} options={opts} onOptionsChange={setOpts} canEdit={canBasicEdit} onClose={() => closeManager(key)} />
                )}
              </div>
            ))}
          </div>

          {/* Row 2: 產品名稱(中文), 產品名稱(英文), 規格, 箱入數 */}
          <div className="pd-form-grid pd-form-grid-4" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <div className="form-group" ref={registerRequiredBox('productName')}>
              <label>產品名稱 (中文) <span className="pd-required-mark">*</span></label>
              <input ref={registerRequiredInput('productName')} type="text" className={requiredClass('productName')} value={form.productName} onChange={(e) => updateField('productName', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="請輸入中文產品名稱" />
              {renderRequiredError('productName')}
            </div>
            <div className="form-group">
              <label>產品名稱 (英文)</label>
              <input type="text" className="form-control" value={form.englishName} onChange={(e) => updateField('englishName', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="用於英文版目錄" />
            </div>
            {/* 需求 R129 Q1=C：規格／箱入數／保存期限是本次新增的必填欄位，補上 * 記號。 */}
            <div className="form-group" ref={registerRequiredBox('spec')}>
              <label>規格 <span className="pd-required-mark">*</span></label>
              <input ref={registerRequiredInput('spec')} type="text" className={requiredClass('spec')} value={form.spec} onChange={(e) => updateField('spec', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="例如: 150g/包" />
              {renderRequiredError('spec')}
            </div>
            <div className="form-group" ref={registerRequiredBox('pcsPerBox')}>
              <label>箱入數 <span className="pd-required-mark">*</span></label>
              <input ref={registerRequiredInput('pcsPerBox')} type="text" inputMode="decimal" className={requiredClass('pcsPerBox')} value={form.pcsPerBox} onChange={(e) => updateField('pcsPerBox', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="每箱數量" />
              {renderRequiredError('pcsPerBox')}
            </div>
          </div>

          {/* 產品標籤 */}
          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                產品標籤 (Tag)
                {!disabled && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-info)', cursor: 'pointer' }} onClick={() => toggleManager('tags')}>+ 管理標籤</span>}
              </label>
              {!disabled && tagOptions.length > 0 && (
                <div className={`tag-dropdown${tagsDropdownOpen ? ' open' : ''}`} ref={tagsDropdownRef}>
                  <div className="tag-dropdown-trigger form-control" style={{ flexWrap: 'wrap', gap: '4px', height: 'auto', minHeight: '36px' }} onClick={() => setTagsDropdownOpen((prev) => !prev)}>
                    {form.tags.length === 0
                      ? <span className="tag-dropdown-placeholder">請選擇標籤（可複選）</span>
                      : form.tags.map((t) => (
                          <span key={t} className="tag" onClick={(e) => e.stopPropagation()} style={{ margin: 0 }}>
                            {t}
                            <button className="tag-remove" onClick={(e) => { e.stopPropagation(); toggleTag(t); }}>×</button>
                          </span>
                        ))
                    }
                    <span className="tag-dropdown-arrow" style={{ marginLeft: 'auto' }}>▾</span>
                  </div>
                  <div className="tag-dropdown-menu">
                    {tagOptions.map((t) => (
                      <label key={t._id} className="tag-dropdown-item">
                        <input type="checkbox" checked={form.tags.includes(t.value)} onChange={() => toggleTag(t.value)} />
                        {t.value}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {/* 需求 R28（PM 2026-05-21）：業務(sales) 無 PRODUCT_BASIC_EDIT，
                  原本只在 form.tags 有值時才渲染唯讀 chips，當該產品本身沒有 tag 時整個區塊
                  完全空白，PM 反映「畫面中標籤的資訊僅呈現標題、無內容」。改為唯讀模式
                  一律渲染：有 tag → 顯示 chips；沒 tag → 顯示「尚未設定標籤」提示文字，
                  避免畫面看起來像功能壞掉。業務仍無法新增/移除/管理 tag。 */}
              {disabled && (
                form.tags.length > 0 ? (
                  <div className="tag-selected" style={{ marginTop: 'var(--spacing-sm)' }}>
                    {form.tags.map((t) => (
                      <span key={t} className="tag">{t}</span>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginTop: 'var(--spacing-sm)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                    尚未設定標籤
                  </div>
                )
              )}
              {!disabled && openManagers['tags'] && (
                <OptionManager type="tag" label="標籤" options={tagOptions} onOptionsChange={setTagOptions} canEdit={canBasicEdit} onClose={() => closeManager('tags')} />
              )}
            </div>
          </div>

          {/* 欄位區塊：保存期限, 國際條碼, 末端價 + 右側產品圖片 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 'var(--spacing-lg)' }}>
            <div className="form-group" ref={registerRequiredBox('shelfLife')}>
              <label>保存期限 <span className="pd-required-mark">*</span></label>
              <input ref={registerRequiredInput('shelfLife')} type="text" className={requiredClass('shelfLife')} value={form.shelfLife} onChange={(e) => updateField('shelfLife', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="例如: 18個月" />
              {renderRequiredError('shelfLife')}
            </div>
            <div className="form-group">
              <label>國際條碼</label>
              <input type="text" className="form-control" value={form.barcode} onChange={(e) => updateField('barcode', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="EAN-13 條碼" />
            </div>
            <div className="form-group">
              <label>末端價</label>
              <input type="text" inputMode="decimal" className="form-control" value={form.retailPrice} onChange={(e) => updateField('retailPrice', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="建議零售價" onBlur={(e) => { if (e.target.value) updateField('retailPrice', fmtMoney(e.target.value)); }} />
            </div>

            {/* 產品圖片（跨2列） */}
            {/* 需求 R87（PM 回饋：高度沒對齊，保留截圖按鈕尺寸、只調上傳區塊高度）：
                掛 pd-image-formgroup 讓此欄改直向 flex，上傳虛線框自動撐滿 label 與截圖按鈕
                以外的剩餘高度，使「上傳框＋截圖按鈕」底部與左側第二列底部切齊（AC2）。 */}
            <div className="form-group pd-image-formgroup" style={{ gridRow: 'span 2', gridColumn: 4 }}>
              <label>產品圖片</label>
              {form.productImage ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={form.productImage} alt="product" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--color-border-light)' }} onError={(e) => { e.target.style.display = 'none'; }} />
                  {!disabled && (
                    <button type="button" style={{ position: 'absolute', top: -6, right: -6, background: 'var(--color-danger)', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, fontSize: 12, cursor: 'pointer', lineHeight: '20px', textAlign: 'center' }} onClick={() => updateField('productImage', '')} title="移除圖片">&#10005;</button>
                  )}
                </div>
              ) : (
                <div className="pd-image-upload-area pd-image-upload-area--compact">
                  <div className="pd-upload-placeholder">
                    <div className="pd-upload-placeholder-icon">{'\u{1F4F7}'}</div>
                    <div>點擊上傳圖片</div>
                    {/* 需求 R87：說明文字加 pd-upload-hint class，於 --compact（詳細頁）模式下
                        隱藏，讓上傳區塊真正縮到 min-height 不被三行內容撐高，使「上傳區塊＋滿版
                        截圖按鈕」合計高度確實與左側兩列欄位對齊（AC2）；ProductModal 未套 compact
                        仍完整顯示此提示，不受影響（AC5）。 */}
                    <div className="pd-upload-hint" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-xs)' }}>
                      支援 jpg/png/webp，自動壓縮至 300KB 以下
                    </div>
                  </div>
                  {!disabled && (
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        // 需求 R89（PM 2026-07-31 拍板）：移除前端 5MB 阻擋，
                        // 讓大檔也能送到後端，由後端 sharp 壓縮至 300KB 後存檔。
                        const formData = new FormData();
                        formData.append('image', file);
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${API}/products/upload-image`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}` },
                            body: formData,
                          });
                          if (res.ok) {
                            const { url } = await res.json();
                            updateField('productImage', url);
                          } else {
                            // 需求 R89：後端超限／格式錯誤會回 JSON { message }。但移除前端 5MB
                            // 阻擋後，大檔也可能先被反向代理／平台擋下並回「非 JSON」的 413 頁面，
                            // 此時 res.json() 會丟出例外、落到下方 catch，使用者只看得到通用
                            // 「上傳失敗」，AC12 要的可讀訊息在最需要的大檔情境反而失效。
                            // 比照其餘 5 個上傳入口加上 .catch fallback。
                            const err = await res.json().catch(() => ({}));
                            alert(err.message || (res.status === 413 ? '圖片檔案過大，請勿超過 50MB' : '上傳失敗'));
                          }
                        } catch (err) {
                          console.error('上傳失敗', err);
                          alert('上傳失敗');
                        }
                        e.target.value = '';
                      }}
                    />
                  )}
                </div>
              )}
              {/* 需求 R86：追加螢幕截圖上傳（保留上方檔案上傳）。PM 2026-07-29
                  拍板方案 A（D1 原生對話框取代自動隱藏／D2 截取後可框選裁切／D3 僅桌面可用）。
                  此為 D5 第二處上傳點 ProductDetailPage，與 ProductModal 使用同一元件。桌面支援
                  getDisplayMedia 才出現按鈕；截取後可於預覽框選要上傳的範圍；手機／不支援的瀏覽器
                  ScreenshotCapture 回傳 null，沿用檔案上傳。 */}
              {/* 需求 R142（PM 2026-08-29）：Q1=A 只在本頁、Q2「比照截圖鈕，
                  只有具產品基本編輯權且非檢視模式才顯示」→ 整個 actions 列沿用同一個
                  !disabled（disabled = isView || !canBasicEdit），與截圖鈕條件完全一致。
                  AC1 一列兩欄：左＝既有螢幕截圖、右＝新的下載圖片。
                  AC2 不動 ScreenshotCapture 內部渲染（它另有 4 個呼叫點），改在外層包一個
                  .pd-image-actions__slot 當格子；ScreenshotCapture 的錯誤訊息 div 也留在
                  這個 slot 裡，才不會變成第三個並排欄把兩欄擠成三欄。
                  AC13／Q3=A 退化排版：不支援 getDisplayMedia 時 ScreenshotCapture 回傳 null，
                  slot 就成為真正的空元素，由 CSS `:empty { display:none }` 收掉，下載鈕再由
                  `:empty + __download { grid-column: 1 / -1 }` 跨兩軌撐成滿版
                  —— 不需要在 JS 這邊偵測瀏覽器支援度。
                  等寬（PM 2026-08-29 回饋「電腦版兩個按鈕大小一致」）：容器是 grid
                  minmax(0,1fr) 兩軌，不是 flex —— flex 版被左欄那顆 nowrap 按鈕的
                  min-content 頂住，實測 123px／149px 不等寬。
                  同高＋圖示文字同尺寸（PM 2026-08-29 第二次回饋）：下載鈕的
                  padding／font-size／line-height 由 CSS `.pd-image-actions
                  .pd-image-actions__download` 兩層選擇器提供 —— 單層 class 會與
                  variables.css 的 `.btn` 平手，而 App.js 把 variables.css 排在各 page 之後
                  import，打包後 `.btn`（padding 8px 24px／font-size 15px）反而贏，
                  下載鈕因此變成 40.5px、截圖鈕 33.5px。詳見 ProductsPage.css
                  .pd-image-actions 的註解與 tests/buttonSize.html 實測頁。
                  按鈕文字寫成單一字串（比照截圖鈕的「📷 螢幕截圖」），避免 `.btn` 的
                  gap 在圖示與文字之間多插一段間距。 */}
              {!disabled && (
                <div className="pd-image-actions">
                  <div className="pd-image-actions__slot">
                    <ScreenshotCapture onUploaded={(url) => updateField('productImage', url)} fullWidth />
                  </div>
                  {/* AC3：僅在已有圖片時顯示；尚無圖片（虛線上傳框）時不渲染。 */}
                  {form.productImage && (
                    <button
                      type="button"
                      className="btn btn-outline pd-image-actions__download"
                      onClick={handleDownloadImage}
                      title="下載這張產品圖片到電腦，檔名為產品名稱"
                      aria-label="下載產品圖片"
                    >
                      ⬇️ 下載圖片
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* 葷素備註 — 需求 R59 D3(A) / D6(B)：純文字自由輸入。
                本任務推翻 需求 R34「僅下拉選單」拍板；PM 2026-07-04 拍板
                D1=C(保留 ProductOption 資料但停用 UI 管理入口)、D3=A、D6=B(移除 R3 UI 防禦
                + R5 掃描 CLI)。此欄不再需要 vegetarianOptions 資料源與 OptionManager 齒輪。 */}
            <div className="form-group">
              <label>葷素備註</label>
              <input
                type="text"
                className="form-control"
                value={form.vegetarianType}
                onChange={(e) => updateField('vegetarianType', e.target.value)}
                readOnly={isView}
                disabled={!isView && disabled}
                placeholder="葷素備註（自由輸入）"
              />
            </div>

            {/* 封箱色 */}
            <div className="form-group">
              <label>封箱色</label>
              <input type="text" className="form-control" value={form.sealingColor} onChange={(e) => updateField('sealingColor', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="外箱封箱膠帶顏色" />
            </div>
            {/* 業務備註 */}
            <div className="form-group">
              <label>業務備註</label>
              <input type="text" className="form-control" value={form.salesNotes} onChange={(e) => updateField('salesNotes', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="業務備註" />
            </div>
          </div>

        </div>
      </section>
    );
  };

  const renderLogistics = () => {
    const disabled = isView || !canLogisticsEdit;
    return (
      <section id="section-物流屬性" className="pd-section-card">
        <div className="pd-section-header">
          <h2 className="pd-section-title">
            <span className="pd-section-icon">{'\u{1F69A}'}</span>
            物流與物理屬性
          </h2>
        </div>
        <div className="pd-section-body">
          <div className="pd-form-grid pd-form-grid-3">
            <div className="form-group" ref={registerRequiredBox('netWeight')}>
              <label>淨重 (kg) <span className="pd-required-mark">*</span></label>
              <input ref={registerRequiredInput('netWeight')} type="text" inputMode="decimal" className={requiredClass('netWeight')} value={form.netWeight} onChange={(e) => updateField('netWeight', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="產品淨重" />
              {renderRequiredError('netWeight')}
            </div>
            <div className="form-group" ref={registerRequiredBox('grossWeight')}>
              <label>毛重 (kg) <span className="pd-required-mark">*</span></label>
              <input ref={registerRequiredInput('grossWeight')} type="text" inputMode="decimal" className={requiredClass('grossWeight')} value={form.grossWeight} onChange={(e) => updateField('grossWeight', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="含包裝總重量" />
              {renderRequiredError('grossWeight')}
            </div>

            {/* 材積 - full width */}
            {/* 需求 R129 Q2=A：材積以最終算出的 volumeCbm > 0 判定有填，三種 volumeMethod 皆可；
                紅框套在整個計算器外框（三格輸入來源不同），focus 落在「當下這一種方法」的輸入格。 */}
            <div className="form-group" style={{ gridColumn: '1 / -1' }} ref={registerRequiredBox('volumeCbm')}>
              <label>材積 <span className="pd-required-mark">*</span></label>
              <div className={`pd-cbm-calculator${isMissing('volumeCbm') ? ' pd-field-invalid' : ''}`}>
                <div className="pd-cbm-table">
                  {/* Header row */}
                  <div className="pd-cbm-header-cell">自動計算</div>
                  <div className="pd-cbm-header-cell">廠商提供</div>
                  <div className="pd-cbm-header-cell" style={{ borderRight: 'none' }}>預估值</div>

                  {/* Input row */}
                  <div className="pd-cbm-input-cell" style={{ borderRight: '1px solid var(--color-border-light)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--spacing-sm)', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                        <span style={{ fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>長</span>
                        <input ref={form.volumeMethod === 'auto' ? registerRequiredInput('volumeCbm') : undefined} type="text" inputMode="decimal" className="form-control" value={form.boxLength} onChange={(e) => setForm((prev) => ({ ...prev, volumeMethod: 'auto', boxLength: e.target.value }))} readOnly={isView} disabled={!isView && disabled} placeholder="cm" />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                        <span style={{ fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>寬</span>
                        <input type="text" inputMode="decimal" className="form-control" value={form.boxWidth} onChange={(e) => setForm((prev) => ({ ...prev, volumeMethod: 'auto', boxWidth: e.target.value }))} readOnly={isView} disabled={!isView && disabled} placeholder="cm" />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                        <span style={{ fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>高</span>
                        <input type="text" inputMode="decimal" className="form-control" value={form.boxHeight} onChange={(e) => setForm((prev) => ({ ...prev, volumeMethod: 'auto', boxHeight: e.target.value }))} readOnly={isView} disabled={!isView && disabled} placeholder="cm" />
                      </div>
                    </div>
                  </div>
                  <div className="pd-cbm-input-cell" style={{ borderRight: '1px solid var(--color-border-light)', justifyContent: 'center' }}>
                    <input
                      ref={form.volumeMethod === 'supplier' ? registerRequiredInput('volumeCbm') : undefined}
                      type="text"
                      inputMode="decimal"
                      className="form-control"
                      value={form.volumeMethod === 'supplier' ? form.volumeCuft : ''}
                      onChange={(e) => setForm((prev) => ({ ...prev, volumeMethod: 'supplier', volumeCuft: e.target.value, boxLength: '', boxWidth: '', boxHeight: '' }))}
                      readOnly={isView} disabled={!isView && disabled}
                      placeholder="輸入數值"
                    />
                  </div>
                  <div className="pd-cbm-input-cell" style={{ justifyContent: 'center' }}>
                    <input
                      ref={form.volumeMethod === 'estimate' ? registerRequiredInput('volumeCbm') : undefined}
                      type="text"
                      inputMode="decimal"
                      className="form-control"
                      value={form.volumeMethod === 'estimate' ? form.volumeCuft : ''}
                      onChange={(e) => setForm((prev) => ({ ...prev, volumeMethod: 'estimate', volumeCuft: e.target.value, boxLength: '', boxWidth: '', boxHeight: '' }))}
                      readOnly={isView} disabled={!isView && disabled}
                      placeholder="輸入數值"
                    />
                  </div>

                  {/* Result row */}
                  <div className="pd-cbm-result-row">
                    <span style={{ fontWeight: 'var(--font-weight-medium)' }}>材積：</span>
                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
                      <span>CBM：</span>
                      <span style={{ fontWeight: 'var(--font-weight-bold)' }}>{form.volumeCbm !== '' && form.volumeCbm != null ? parseFloat(form.volumeCbm).toFixed(3) : '-'}</span>
                    </div>
                    <span style={{ color: 'var(--color-border)' }}>|</span>
                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
                      <span>CUFT：</span>
                      <span style={{ fontWeight: 'var(--font-weight-bold)' }}>{form.volumeCuft !== '' && form.volumeCuft != null ? parseFloat(form.volumeCuft).toFixed(2) : '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
              {renderRequiredError('volumeCbm')}
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderCost = () => {
    const disabled = isView || !canBasicEdit;

    const renderCostEntry = (entry, onChange, label, onRemove, costSourceValue) => (
      <div className="pd-cost-input-group" style={{ marginBottom: 'var(--spacing-md)', border: form.costSource === costSourceValue ? '2px solid var(--color-primary)' : undefined }}>
        <div className="pd-cost-input-header">
          <span className="pd-cost-input-title">{label}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
            <label className="pd-cost-radio">
              <input type="radio" name="cost-source" checked={form.costSource === costSourceValue} onChange={() => updateField('costSource', costSourceValue)} disabled={disabled} /> 使用此成本報價
            </label>
            {onRemove && !disabled && (
              <button type="button" onClick={onRemove} style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--color-border)', background: 'var(--color-background-light)', color: 'var(--color-danger)', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="刪除此經銷成本">&#10005;</button>
            )}
          </div>
        </div>
        <div style={{ padding: 'var(--spacing-md)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-md)' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 'var(--font-size-xs)' }}>供應商名稱</label>
              <input type="text" className="form-control" value={entry.supplierName} onChange={(e) => onChange('supplierName', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="供應商名稱" />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 'var(--font-size-xs)' }}>成本金額（箱價）</label>
              <input type="text" inputMode="decimal" className="form-control" value={entry.boxPrice} onChange={(e) => onChange('boxPrice', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="箱價" onBlur={(e) => { if (e.target.value) onChange('boxPrice', fmtMoney(e.target.value)); }} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 'var(--font-size-xs)' }}>成本金額（單價）</label>
              <input type="text" className="form-control input-computed" value={fmtMoney(entry.unitPrice)} disabled placeholder="自動計算" />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 'var(--font-size-xs)' }}>MOQ</label>
              <input type="text" className="form-control" value={entry.moq} onChange={(e) => onChange('moq', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="最低訂購量" />
            </div>
          </div>
          {/* 需求 R44（PM 2026-06-01 拍板方案 B）：供應商備註獨立成第二列 full-width textarea，依內容自動拉高。 */}
          <div className="form-group" style={{ marginBottom: 'var(--spacing-md)' }}>
            <label style={{ fontSize: 'var(--font-size-xs)' }}>供應商備註</label>
            {isView ? (
              <div className="form-control pd-notes-view" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', minHeight: 38 }}>{entry.notes || ' '}</div>
            ) : (
              <textarea
                ref={(el) => {
                  if (el) {
                    el.style.height = 'auto';
                    el.style.height = `${el.scrollHeight}px`;
                  }
                }}
                className="form-control"
                value={entry.notes}
                onChange={(e) => {
                  onChange('notes', e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                disabled={disabled}
                placeholder="供應商備註"
                rows={1}
                style={{ resize: 'vertical', overflow: 'hidden', minHeight: 38, lineHeight: 1.5 }}
              />
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 'var(--font-size-xs)' }}>可出貨國家</label>
              <div className="pd-tags-container" style={{ minHeight: 38 }}>
                {(entry.shippableCountries || []).map((c, i) => (
                  <span className="pd-tag" key={i}>
                    <span className="pd-tag-text">{c}</span>
                    {!disabled && <span className="pd-tag-remove" onClick={() => { const arr = [...entry.shippableCountries]; arr.splice(i, 1); onChange('shippableCountries', arr); }}>&#10005;</span>}
                  </span>
                ))}
                {!disabled && <input type="text" style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, minWidth: 80 }} placeholder="輸入國家後按 Enter..." onKeyDown={(e) => { if (e.nativeEvent.isComposing) return; if (e.key === 'Enter') { e.preventDefault(); const v = e.target.value.trim(); if (v) { onChange('shippableCountries', [...(entry.shippableCountries || []), v]); e.target.value = ''; } } }} />}
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 'var(--font-size-xs)' }}>不可出貨國家</label>
              <div className="pd-tags-container" style={{ minHeight: 38 }}>
                {(entry.nonShippableCountries || []).map((c, i) => (
                  <span className="pd-tag" key={i}>
                    <span className="pd-tag-text">{c}</span>
                    {!disabled && <span className="pd-tag-remove" onClick={() => { const arr = [...entry.nonShippableCountries]; arr.splice(i, 1); onChange('nonShippableCountries', arr); }}>&#10005;</span>}
                  </span>
                ))}
                {!disabled && <input type="text" style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, minWidth: 80 }} placeholder="輸入國家後按 Enter..." onKeyDown={(e) => { if (e.nativeEvent.isComposing) return; if (e.key === 'Enter') { e.preventDefault(); const v = e.target.value.trim(); if (v) { onChange('nonShippableCountries', [...(entry.nonShippableCountries || []), v]); e.target.value = ''; } } }} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <section id="section-成本價格" className="pd-section-card">
        <div className="pd-section-header">
          <h2 className="pd-section-title">
            <span className="pd-section-icon">{'\u{1F4B0}'}</span>
            成本與階梯價格
          </h2>
        </div>
        <div className="pd-section-body">
          <h4 style={{ marginTop: 'var(--spacing-lg)', marginBottom: 'var(--spacing-md)', color: 'var(--color-text-primary)' }}>
            標準成本 (報價來源)
          </h4>

          {renderCostEntry(form.factoryCost, (field, val) => updateFactoryCost(field, val), '工廠成本', null, 'factory')}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
            <h4 style={{ margin: 0, color: 'var(--color-text-primary)' }}>經銷成本</h4>
            {!disabled && (
              <button type="button" onClick={addDistributor} disabled={form.distributorCosts.length >= 3} style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--color-border)', background: 'var(--color-background-light)', color: 'var(--color-primary-dark)', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="新增經銷成本">+</button>
            )}
          </div>

          {form.distributorCosts.length === 0 && (
            <div style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', padding: 'var(--spacing-md)', textAlign: 'center', border: '1px dashed var(--color-border)', borderRadius: 'var(--border-radius-md)', marginBottom: 'var(--spacing-md)' }}>
              尚無經銷成本
            </div>
          )}

          {form.distributorCosts.map((d, idx) =>
            renderCostEntry(d, (field, val) => updateDistributorCost(idx, field, val), `經銷成本${form.distributorCosts.length > 1 ? ` #${idx + 1}` : ''}`, () => removeDistributor(idx), `distributor_${idx}`)
          )}

          <h4 style={{ marginTop: 'var(--spacing-lg)', marginBottom: 'var(--spacing-md)', color: 'var(--color-text-primary)' }}>
            採購參考資訊
          </h4>

          <div className="pd-form-grid pd-form-grid-3" style={{ marginBottom: 'var(--spacing-md)' }}>
            <div className="form-group">
              <label>採購成本（單價）</label>
              <input type="text" inputMode="decimal" className="form-control" value={form.purchaseCostUnit} onChange={(e) => updateField('purchaseCostUnit', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="實際採購成本" onBlur={(e) => { if (e.target.value) updateField('purchaseCostUnit', fmtMoney(e.target.value)); }} />
            </div>
            <div className="form-group">
              <label>工廠是否可貼標</label>
              <select className="form-control" value={form.labelable} onChange={(e) => updateField('labelable', e.target.value)} disabled={disabled}>
                <option value="">請選擇</option>
                <option value="yes">可以</option>
                <option value="no">不可以</option>
              </select>
            </div>
            <div className="form-group">
              <label>貼標費用</label>
              <input type="text" className="form-control" value={form.labelFee} onChange={(e) => updateField('labelFee', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="例如：$2/件" />
            </div>
          </div>
          <div className="form-group">
            <label>採購備註</label>
            <input type="text" className="form-control" value={form.purchaseNotes} onChange={(e) => updateField('purchaseNotes', e.target.value)} readOnly={isView} disabled={!isView && disabled} placeholder="採購備註" />
          </div>
        </div>
      </section>
    );
  };

  const renderHK = () => {
    const isManager = role === 'manager';
    const isPurchaser = role === 'purchasing';
    const canEditMarkup = isManager || isPurchaser;
    const displayInt = (computed, stored) => {
      const v = computed !== undefined && computed !== '' ? computed : stored;
      if (v === '' || v === null || v === undefined) return '—';
      const n = parseFloat(v);
      if (isNaN(n)) return String(v);
      return n.toFixed(1);
    };
    const displayDec1 = (computed, stored) => {
      const v = computed !== undefined && computed !== '' ? computed : stored;
      if (v === '' || v === null || v === undefined) return '—';
      const n = parseFloat(v);
      if (isNaN(n)) return String(v);
      return n.toFixed(1);
    };

    return (
      <section id="section-香港專用" className="pd-section-card">
        <div className="pd-section-header">
          <h2 className="pd-section-title">
            <span className="pd-section-icon">{'\u{1F1ED}\u{1F1F0}'}</span>
            香港專用欄位
          </h2>
          <span className="pd-section-badge pd-badge-info">香港公司作業使用</span>
        </div>
        <div className="pd-section-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-md)' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                參考價格 <span className="hk-currency-badge">HKD</span>
              </label>
              <input type="text" className="form-control input-computed" value={displayInt(hkValues.hkReferencePrice, form.hkReferencePrice)} disabled />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                出貨成本（包）<span className="hk-currency-badge">HKD</span>
              </label>
              <input type="text" className="form-control input-computed" value={displayInt(hkValues.hkShippingCostPerPack, form.hkShippingCostPerPack)} disabled />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                出貨成本（箱）<span className="hk-currency-badge">HKD</span>
              </label>
              <input type="text" className="form-control input-computed" value={displayInt(hkValues.hkShippingCostPerBox, form.hkShippingCostPerBox)} disabled />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                台灣進價 <span className="hk-currency-badge">HKD</span>
              </label>
              <input type="text" className="form-control input-computed" value={displayInt(hkValues.hkTaiwanPrice, form.hkTaiwanPrice)} disabled />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 'var(--spacing-md)' }}>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                物流成本 <span className="hk-currency-badge">HKD</span>
              </label>
              <input type="text" className="form-control input-computed" value={displayDec1(hkValues.hkLogisticsCost, form.hkLogisticsCost)} disabled />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                進倉成本 <span className="hk-currency-badge">HKD</span>
              </label>
              <input type="text" className="form-control input-computed" value={displayDec1(hkValues.hkInboundCost, form.hkInboundCost)} disabled />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                船務成本 <span className="hk-currency-badge">HKD</span>
              </label>
              <input type="text" className="form-control input-computed" value={displayDec1(hkValues.hkShippingFee, form.hkShippingFee)} disabled />
            </div>
            <div className="form-group">
              <label>香港內部匯率</label>
              <input type="text" className="form-control input-computed" value={hkdRate || '—'} disabled />
            </div>
            <div className="form-group">
              <label>產品加價</label>
              <div style={{ height: 38, display: 'flex', alignItems: 'center', padding: '0 var(--spacing-md)', backgroundColor: 'var(--color-background)', border: '1px solid var(--color-border)', borderRadius: 'var(--border-radius-sm)' }}>
                <label className="pd-checkbox-inline" style={{ margin: 0 }}>
                  <input type="checkbox" checked={form.hkMarkup} onChange={(e) => canEditMarkup && !isView && updateField('hkMarkup', e.target.checked)} disabled={!canEditMarkup || isView} />
                  <span style={{ fontSize: 'var(--font-size-sm)' }}>勾選後此產品加價 5%</span>
                </label>
              </div>
            </div>
          </div>

        </div>
      </section>
    );
  };

  const renderMarketing = () => {
    // 需求 R4（PM 2026-05-08 補充指示）：
    // 「產品狀態（上架/下架）」與「香港不出」需開放給採購編輯，
    // 但「目錄/官網狀態」仍維持僅主管可編輯。disabled 判斷依欄位拆開。
    const productStatusDisabled = isView || !canProductStatusEdit;
    const disabled = isView || !canMarketingEdit;
    const hkExcludedDisabled = isView || !canHkExcludedEdit;
    return (
      <section id="section-行銷狀態" className="pd-section-card">
        <div className="pd-section-header">
          <h2 className="pd-section-title">
            <span className="pd-section-icon">{'\u{1F4CA}'}</span>
            行銷與目錄狀態
          </h2>
        </div>
        <div className="pd-section-body">
          {/* 需求 R128 R3（PM 2026-08-21 第三輪留言逐字：「查看截圖，請重新配置欄位寬度」）：
              本區塊原本是 pd-form-grid-3（三欄等寬 1fr）。R1 把官網狀態由三選一改四選一後，
              中間欄要塞「目錄狀態 +（要上官網／已上官網／不上官網／官網需下架）」共 5 顆，
              headless Chrome 實測自然寬 472px，但 1fr 只給 418px → 溢出 44px，第四顆
              「官網需下架」壓到右邊「香港不出」欄上（PM 截圖現象）。
              修法＝重新配置欄寬（不縮字級、不改色）：改套 .pd-marketing-status-grid，
              依三欄實際內容需求（160 / 472 / 233px）分配比例，見 ProductsPage.css。 */}
          <div className="pd-form-grid pd-form-grid-3 pd-marketing-status-grid">
            <div className="form-group">
              <label>產品狀態</label>
              <div className="pd-status-selector">
                <div className={`pd-status-option${form.productStatus === 'listed' ? ' active' : ''}`} onClick={() => !productStatusDisabled && updateField('productStatus', form.productStatus === 'listed' ? '' : 'listed')}>
                  <span style={{ fontSize: 'var(--font-size-sm)' }}>{'\u2713'} 上架</span>
                </div>
                <div className={`pd-status-option${form.productStatus === 'delisted' ? ' inactive' : ''}`} onClick={() => !productStatusDisabled && updateField('productStatus', form.productStatus === 'delisted' ? '' : 'delisted')}>
                  <span style={{ fontSize: 'var(--font-size-sm)' }}>{'\u2715'} 下架</span>
                </div>
              </div>
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <div>
                  <label>目錄狀態</label>
                  <div className="pd-status-selector">
                    <div
                      className={`pd-status-option${form.catalogStatus === 'in_catalog' ? ' active' : ''}`}
                      onClick={() => !disabled && updateField('catalogStatus', form.catalogStatus === 'in_catalog' ? '' : 'in_catalog')}
                    >
                      <span style={{ fontSize: 'var(--font-size-sm)' }}>上目錄</span>
                    </div>
                  </div>
                </div>
                <div>
                  <label>官網狀態</label>
                  <div className="pd-status-selector">
                    {[
                      { value: 'pending_website', label: '要上官網', cls: 'active' },
                      { value: 'on_website',      label: '已上官網', cls: 'active' },
                      // 需求 R128（PM 2026-08-21）：三選一 → 四選一；「不上行銷」改回「不上官網」（值不動）。
                      // R2（PM 第二輪留言逐字：「官網需下架，顏色配置與『要上官網』相同。」）：
                      // 本頁「要上官網」選中時套 .pd-status-option.active（綠，見上方第一列），
                      // 故 .delist-website 的色值已在 ProductsPage.css 改成與 .active 逐值相同。
                      // class 名稱保留獨立，日後 PM 若要拆色只改 CSS、不必動這裡。
                      { value: 'not_on_website',  label: '不上官網', cls: 'not-marketing' },
                      { value: 'delist_website',  label: '官網需下架', cls: 'delist-website' },
                    ].map(({ value, label, cls }) => (
                      <div
                        key={value}
                        className={`pd-status-option${form.websiteStatus === value ? ` ${cls}` : ''}`}
                        onClick={() => !disabled && updateField('websiteStatus', form.websiteStatus === value ? '' : value)}
                      >
                        <span style={{ fontSize: 'var(--font-size-sm)' }}>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>香港不出</label>
              <div style={{ height: 38, display: 'flex', alignItems: 'center', padding: '0 var(--spacing-md)', backgroundColor: 'var(--color-background)', border: '1px solid var(--color-border)', borderRadius: 'var(--border-radius-sm)' }}>
                <label className="pd-checkbox-inline" style={{ margin: 0 }}>
                  <input type="checkbox" checked={form.hkExcluded} onChange={(e) => !hkExcludedDisabled && updateField('hkExcluded', e.target.checked)} disabled={hkExcludedDisabled} />
                  <span style={{ fontSize: 'var(--font-size-sm)' }}>勾選後此產品不會銷售至香港</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  // 需求 R147「歷史報價」（PM 2026-08-29 原始需求 + Q1-Q9 拍板）
  //
  // PM 原文：「產品詳細資訊頁面裡增加歷史報價區域，名為：歷史報價。以列表方式列出，
  // 一次列出 10 筆，有載入更多按鈕，一次載入追加至多 20 筆。清單顯示內容如下：
  // 報價單編號、客戶名稱、幣別、匯率、階數、報價單價。其中報價單編號可點選後引導至
  // 該報價單。位置：行銷與附錄狀態下方。」（「行銷與附錄狀態」＝本頁的「行銷與目錄狀態」，
  // 系統中沒有叫「附錄」的區塊。）
  //
  // 拍板重點：
  // Q1=A 位置＝行銷狀態之後、稽核紀錄之前（見上面 TABS）。
  // Q2=A 全部狀態都列，列上出狀態標籤。
  // Q3=B 採購看得到但不可點連結；業務看全公司的單（後端不過濾 createdBy）。
  // Q5=A 同一張單有同產品多筆品項就逐筆列出（同一報價單編號會出現多列）——
  // 所以 key 必須是 quoteId+itemId，只用 quoteId 會 React duplicate key。
  // Q6   10 → +20 → +20…，並在標題旁顯示總筆數。
  // Q8   單價位數／幣別完全依照報價單（fmtQuoteHistoryPrice，同 quoteMoney.js）。
  // Q9   **不加**報價日期欄（PM：「不需要，依照我要求的顯示」）——後端有回 quoteDate，
  // 但這裡刻意不渲染；日後想加請先回頭確認 PM 是否改判。
  //
  // AC16 唯讀：本區塊完全不看 isView／不呼叫 updateField、不進 form state，
  // 切到編輯模式時外觀與行為都不變（沒有任何 input／select）。
  const renderQuoteHistory = () => {
    // 需求 R151 R2 第③層（PM 2026-09-01 逐字：「本輪要針對Q8 做修正，
    // 採購看不到「歷史報價區塊」因此也不會看到警示符號」）：TABS 已經把採購擋在外面
    // （visible: !isCreate && canViewQuoteHistory），這裡再擋一次是防止日後有人把本
    // renderer 從別的地方叫起來——整個 <section> 連同「!」「$」一起不存在，不是隱藏。
    if (!canViewQuoteHistory) return null;
    // Q3=B：採購（purchasing）看得到內容，但報價單編號不做成連結。
    // 走到這一行時 role 必定不是 purchasing（上面已 return null），所以 canOpenQuote
    // 在 R2 之後恆為 true；**刻意保留**這個分支：它是 需求 R147 Q3=B／本票 Q3=A 的
    // 決議留痕（採購即使哪天又看得到區塊，也一樣拿不到報價單入口），移除等於把那條
    // 決議一併刪掉。fixture 229 G2 與 233 [2j][2k] 都逐字鎖住它。
    const canOpenQuote = role !== 'purchasing';
    const hasMore = quoteHistory.length < quoteHistoryTotal;
    return (
      <section id="section-歷史報價" className="pd-section-card">
        <div className="pd-section-header">
          <h2 className="pd-section-title">
            <span className="pd-section-icon">{'\u{1F4DC}'}</span>
            歷史報價
            {/* Q6：「是的，顯示總筆數」——標題文字本身維持「歷史報價」（AC1），
                總筆數以獨立 badge 呈現。 */}
          </h2>
          {/* 需求 R153（PM 2026-08-31 逐字：「顏色不動，但請跟香港公司作業使用
              一樣向右對齊」）：徽章原本寫在 <h2 className="pd-section-title"> 裡面，而
              .pd-section-title 是 display:flex + gap，所以徽章被當成標題的一部分緊貼在
              「歷史報價」右邊＝靠左。:926「一般權限」與 :1488「香港公司作業使用」兩顆則是
              .pd-section-header（display:flex; justify-content:space-between）的**直接子元素**，
              才會被推到最右緣。本票的修法就是把這顆搬出 h2 成為 header 的第二個子元素，
              與那兩顆結構完全一致——class 一字不改（仍是 pd-section-badge pd-badge-info），
              CSS 一行不動，故顏色／字級／圓角／內距全部維持原樣。 */}
          <span className="pd-section-badge pd-badge-info">共 {quoteHistoryTotal} 筆</span>
        </div>
        <div className="pd-section-body">
          {quoteHistory.length === 0 ? (
            // AC14：查無資料時區塊仍然顯示（不隱藏），出空狀態文字、且不出「載入更多」。
            // 比照 renderAudit 的「尚無變動紀錄」寫法。載入中先出載入字樣，避免第一秒
            // 閃一下「尚無歷史報價紀錄」再跳出資料。
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>
              {quoteHistoryLoading ? '載入中…' : '尚無歷史報價紀錄'}
            </div>
          ) : (
            <>
              <table className="pd-price-table pd-quote-history-table">
                <thead>
                  <tr>
                    {/* AC3：六欄固定，順序與 PM 原文一致。 */}
                    <th>報價單編號</th>
                    <th>客戶名稱</th>
                    <th>幣別</th>
                    <th>匯率</th>
                    <th>階數</th>
                    <th>報價單價</th>
                  </tr>
                </thead>
                <tbody>
                  {quoteHistory.map((row) => {
                    const st = QUOTE_HISTORY_STATUS[row.status] || null;
                    return (
                      // Q5=A：同單多品項會有相同 quoteId，key 必須併上 itemId。
                      <tr key={`${row.quoteId}-${row.itemId}`}>
                        <td>
                          {canOpenQuote ? (
                            // 需求 R151 需求 1（PM 2026-08-31 Q1=A 逐字：「把歷史報價單號導向
                            // 改成 /quotes/:id/edit，與列表『檢視』完全同一頁同一元件」）：
                            // **推翻** 需求 R147 AC8 的 /quotes/:id。報價單列表「檢視」鈕
                            // （QuotesPage.js:536-541）連的就是 /quotes/:id/edit ——「檢視」與
                            // 「編輯」是同一支 QuoteEditPage，唯讀與否由該頁 isLocked 決定
                            // （quoteStatus==='approved' || (pending && !isManager)），本票
                            // 不新增任何唯讀旁路參數（AC3）。
                            // 導向方式維持同分頁 navigate＋Enter 鍵（AC2，沿用 需求 R147 的互動）。
                            <span
                              className="pd-quote-link"
                              role="link"
                              tabIndex={0}
                              onClick={() => navigate(`/quotes/${row.quoteId}/edit`)}
                              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/quotes/${row.quoteId}/edit`); }}
                            >
                              {row.quoteNumber || '—'}
                            </span>
                          ) : (
                            // Q3=B：採購看得到單號，但不給連結、不給 cursor:pointer。
                            <span className="pd-quote-nolink" title="採購無報價單檢視權限">{row.quoteNumber || '—'}</span>
                          )}
                          {/* Q2=A：全部狀態都列，故每列都要看得出這張單目前是什麼狀態。 */}
                          {st && <span className={`pd-qh-status ${st.className}`}>{st.label}</span>}
                        </td>
                        {/* AC9：後端已處理 customer ref 優先／customerName 快照 fallback，
                            兩者皆無時回空字串 → 這裡補 '—'。 */}
                        <td>{row.customerName || '—'}</td>
                        <td>{row.currency || '—'}</td>
                        {/* AC10：匯率照該單存值原樣顯示（含自訂匯率的單），
                            與 QuoteDetailPage.js:401 的 {quote.exchangeRate} 一致，不另外湊小數位。 */}
                        <td>{row.exchangeRate === null || row.exchangeRate === undefined ? '—' : row.exchangeRate}</td>
                        <td>{row.priceTier || '—'}</td>
                        <td className="pd-quote-price">
                          {/* Q8「完全依照報價單顯示，也顯示幣別」：位數規則同報價單詳情頁，
                              並在數字旁帶幣別。AC11：讀 DB 存值不重算。 */}
                          {row.unitPrice === null || row.unitPrice === undefined
                            ? '—'
                            : `${row.currency || ''} ${fmtQuoteHistoryPrice(row.unitPrice, row.currency)}`.trim()}
                          {/* 需求 R151 需求 2＋Q9=A：兩顆警示符號就放在「報價單單價」欄儲存格內、
                              數字右側，順序「!」在前「$」在後（與 QuoteEditPage.js 的排列一致，
                              不另開一欄）。Q6=A：兩顆都是純提示 —— 不掛 onClick、不觸發任何寫入。
                              Q5=C：「!」**不給 tooltip**（所以也沒有「點擊還原」的字樣問題，
                              本區塊唯讀、點了本來就不會還原）。 */}
                          {hasQuoteHistoryPriceModified(row, fullProduct) && (
                            <span className="pd-quote-modified-dot">!</span>
                          )}
                          {/* Q7=A：基準時間逐列各自比自己那張單的最後儲存時間（row.quoteUpdatedAt），
                              不是全區塊共用一個時間 → 與在該報價單頁看到的完全一致（AC8）。
                              AC12：tooltip 沿用 SENSITIVE_PRICE_ALERT_TITLE 同一句，不列欄位／
                              前後值／修改人／時間、不夾帶金額（需求 R148 Q5=C）。 */}
                          {hasSensitivePriceChange(fullProduct, row.quoteUpdatedAt) && (
                            <span className="price-alert-dot" title={SENSITIVE_PRICE_ALERT_TITLE}>$</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* AC6：載完（已載入列數 ≥ total）就不再出按鈕；AC7：載入中 disabled。 */}
              {hasMore && (
                <div style={{ textAlign: 'center', marginTop: 'var(--spacing-md)' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    disabled={quoteHistoryLoading}
                    onClick={() => fetchQuoteHistory(quoteHistory.length)}
                  >
                    {quoteHistoryLoading ? '載入中…' : `載入更多（已顯示 ${quoteHistory.length} / ${quoteHistoryTotal} 筆）`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    );
  };

  const renderAudit = () => {
    const allLog = fullProduct?.auditLog || [];
    // 需求 R5（PM 2026-05-08 後續追加要求）：
    // 稽核紀錄需顯示「工廠成本(箱價)」、「經銷成本(箱價)」與「採購成本(單價)」。
    // 末端價不需追蹤；其他舊欄位（英文品名、業務備註等）也不在此區塊呈現。
    //
    // 需求 R146（PM 2026-08-29 留言「本案可結案，僅是盤點現況」＝Q1=A）：
    // 已完成「價格敏感紀錄」全面盤點，結論＝維持現況、本輪不擴充追蹤範圍。
    // 顯示中（3 類）：工廠成本(箱價)／經銷成本#N(箱價)／採購成本(單價)。
    // 寫入路徑（2 條有效）：PUT /api/products/:id、Excel 匯入覆蓋 buildPriceAuditEntries；
    // 一次性 CLI importProducts 因無登入者刻意不寫（需求 R109 Q5）。
    // 已知未追蹤（PM 未拍要補，需另開票）：衍生 unitPrice、retailPrice、costSource、
    // supplierName／MOQ／出貨國家、香港區塊 6 個價格、報價單端全部價格（Quote 無 auditLog）。
    //
    // 需求 R164（PM 2026-09-07 逐字留言「任務解讀方向錯誤，需求是：若材積變動，
    // 成本就會變動，這樣『＄』符號也要顯示」）：追蹤／顯示範圍由 3 類擴張為 4 類，新增
    // 材積(CUFT)：材積是報價單單價公式的直接輸入（係數 × 材積 ÷ 箱入數，一般 16／冷凍 23），
    // 改材積等於改這張單的成本，因此既要進稽核表格，也要讓報價單的「$」亮起來。
    // 只追蹤 volumeCuft 一個值：volumeCbm 由它換算而得（calcCbm）、箱規長寬高在
    // auto 模式下也只是用來算出 volumeCuft，追這一個值即可涵蓋三條輸入路徑，
    // 且「箱規改了但材積換算後同值」＝成本沒變 → 正確地不寫紀錄、不亮「$」。
    //
    // 下面的條件與 ProductModal.js、scripts/cleanAuditLog.js 的 shouldKeep、
    // utils/sensitivePriceAlert.js、server utils/sensitivePriceAudit.js 五處必須同步；
    // tests/priceAuditScope.fixture.js 與 tests/volumeCostAudit.fixture.js
    // 會鎖住這個範圍，擅自增刪會紅燈。
    const log = allLog.filter((entry) => {
      const field = entry.field || '';
      const isCostBox = (field.startsWith('工廠成本') || field.startsWith('經銷成本')) && field.endsWith('(箱價)');
      const isPurchaseUnit = field === '採購成本(單價)';
      const isVolume = field === '材積(CUFT)';
      return isCostBox || isPurchaseUnit || isVolume;
    });
    return (
      <section id="section-異動紀錄" className="pd-section-card">
        <div className="pd-section-header">
          <h2 className="pd-section-title">
            <span className="pd-section-icon">{'\u{1F4CB}'}</span>
            稽核紀錄
          </h2>
        </div>
        <div className="pd-section-body">
          <h4 style={{ marginBottom: 'var(--spacing-md)' }}>價格敏感紀錄</h4>
          {log.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>尚無變動紀錄</div>
          ) : (
            <table className="pd-price-table">
              <thead>
                <tr>
                  <th>日期</th>
                  <th>修改人</th>
                  <th>欄位</th>
                  <th>修改前</th>
                  <th>修改後</th>
                </tr>
              </thead>
              <tbody>
                {[...log].reverse().map((entry) => (
                  <tr key={entry._id}>
                    <td>{formatDate(entry.changedAt)}</td>
                    <td>{entry.changedBy?.name || '—'}</td>
                    <td>{fmtAuditVal(entry.field)}</td>
                    <td>{fmtAuditVal(entry.oldValue)}</td>
                    <td>{fmtAuditVal(entry.newValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    );
  };

  const sectionRenderers = {
    '基本資訊': renderBasicInfo,
    '物流屬性': renderLogistics,
    '行銷狀態': renderMarketing,
    '成本價格': renderCost,
    '香港專用': renderHK,
    // 需求 R147 AC2：TABS／sectionRenderers／section id 三處缺一不可 —— 少了這一行，
    // 錨點導覽點得到但頁面上不會有區塊（visibleTabs.map 靠 label 查這張表）。
    '歷史報價': renderQuoteHistory,
    '異動紀錄': renderAudit,
  };

  const scrollToSection = (label) => {
    setActiveNav(label);
    const el = document.getElementById(`section-${label}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="products-page" ref={pageRef}>
      {/* Page Header */}
      <header className="pd-page-header" ref={headerRef}>
        <div className="pd-header-content">
          <div className="pd-header-left">
            <button className="pd-back-btn" onClick={() => navigate('/products')}>
              <span>{'\u2190'}</span> 返回列表
            </button>
            <div>
              <h1 className="pd-page-title">{isCreate ? '新增產品' : '產品詳細資訊'}</h1>
              {!isCreate && headerSubtitle && (
                <span className="pd-product-code">{headerSubtitle}</span>
              )}
            </div>
          </div>
          <div className="pd-header-actions">
            {isView ? (
              canBasicEdit && <button className="btn btn-primary" onClick={enableEdit}>{'\u270F\uFE0F'} 編輯</button>
            ) : (
              <>
                <button className="btn btn-outline" onClick={cancelEdit}>取消</button>
                <button className="btn btn-primary" onClick={() => handleSave()}>儲存變更</button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="pd-container">
        {/* Section Navigation */}
        <nav className="pd-tab-nav">
          {visibleTabs.map((tab) => (
            <button
              key={tab.label}
              className={`pd-tab-item${activeNav === tab.label ? ' active' : ''}`}
              onClick={() => scrollToSection(tab.label)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* All Sections */}
        <div className={isView ? 'product-view-mode' : ''}>
          {visibleTabs.map((tab) => (
            <React.Fragment key={tab.label}>
              {sectionRenderers[tab.label]?.()}
            </React.Fragment>
          ))}
        </div>

        {/* Footer Actions */}
        {!isView && (
          <div className="pd-footer-actions">
            <div style={{ flex: 1 }} />
            <button className="btn btn-outline" onClick={cancelEdit}>取消變更</button>
            <button className="btn btn-secondary" onClick={() => handleSave()}>儲存草稿</button>
            <button className="btn btn-primary" onClick={() => handleSave({ returnToList: true })}>儲存並返回</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
